<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import PipelineCardsAPI from 'dashboard/api/pipelineCards';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import StartSequenceModal from './StartSequenceModal.vue';

const props = defineProps({
  cardId: {
    type: [Number, String],
    required: true,
  },
});

const emit = defineEmits(['sequenceStarted']);

const { t } = useI18n();
const store = useStore();
const { showAlert } = useAlert();
const { isCloudFeatureEnabled } = useAccount();
const sequences = ref([]);
const isLoading = ref(false);
const showStartModal = ref(false);
const completingStepId = ref(null);

// Audit 2026-05-07 — feature flag pipeline_sequences pode estar desligada
// para esta account (SuperAdmin). Quando desligada: nada renderiza, nada
// é fetchado, badges não aparecem.
const isSequencesEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.PIPELINE_SEQUENCES)
);

const activeSequences = computed(() =>
  sequences.value.filter(s => s.status === 'active' || s.status === 'paused')
);

const completedSequences = computed(() =>
  sequences.value.filter(
    s => s.status === 'completed' || s.status === 'cancelled'
  )
);

const fetchSequences = async () => {
  if (!isSequencesEnabled.value) return;
  isLoading.value = true;
  try {
    const { data } = await PipelineCardsAPI.getCardSequences(props.cardId);
    sequences.value = data.data || [];
    // Sync the kanban badge cache so PipelineCard badge updates reactively.
    store.dispatch('pipelineCardSequences/setCardSequences', {
      cardId: props.cardId,
      sequences: sequences.value,
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to fetch sequences:', error);
  } finally {
    isLoading.value = false;
  }
};

// Pause/resume atualizam o array local E o cache do badge do kanban — sem o
// dispatch, o badge do board ficava com status/cor antigos enquanto o painel
// de detalhe já mostrava o novo estado (achado Codex, ciclo 3a).
const syncBadgeCache = () => {
  store.dispatch('pipelineCardSequences/setCardSequences', {
    cardId: props.cardId,
    sequences: sequences.value,
  });
};

const handlePause = async seq => {
  try {
    const { data } = await PipelineCardsAPI.pauseSequence(props.cardId, seq.id);
    const idx = sequences.value.findIndex(s => s.id === seq.id);
    if (idx !== -1) sequences.value[idx] = data.data;
    syncBadgeCache();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to pause sequence:', error);
  }
};

const handleResume = async seq => {
  try {
    const { data } = await PipelineCardsAPI.resumeSequence(
      props.cardId,
      seq.id
    );
    const idx = sequences.value.findIndex(s => s.id === seq.id);
    if (idx !== -1) sequences.value[idx] = data.data;
    syncBadgeCache();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to resume sequence:', error);
  }
};

const handleCancel = async seq => {
  // eslint-disable-next-line no-restricted-globals, no-alert
  if (!window.confirm(t('PIPELINE.SEQUENCES.CONFIRM_CANCEL'))) return;
  try {
    await PipelineCardsAPI.cancelSequence(props.cardId, seq.id);
    await fetchSequences();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to cancel sequence:', error);
  }
};

// Phase 1.2 — Mark current step completed and advance to next.
// Backend endpoint created by Phase 1.1 agent. May 404 during parallel dev.
const handleCompleteStep = async seq => {
  completingStepId.value = seq.id;
  try {
    const { data } = await PipelineCardsAPI.completeSequenceStep(
      props.cardId,
      seq.id
    );
    const idx = sequences.value.findIndex(s => s.id === seq.id);
    if (idx !== -1) sequences.value[idx] = data.data;
    // Refresh list to get updated step counts and potentially new status
    await fetchSequences();
    showAlert(t('PIPELINE.SEQUENCES.COMPLETE_STEP_SUCCESS'));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to complete sequence step:', error);
    showAlert(t('PIPELINE.SEQUENCES.COMPLETE_STEP_ERROR'));
  } finally {
    completingStepId.value = null;
  }
};

const formatNextStep = nextStepAt => {
  if (!nextStepAt) return null;
  try {
    return formatDistanceToNow(new Date(nextStepAt), {
      addSuffix: true,
      locale: ptBR,
    });
  } catch {
    return nextStepAt;
  }
};

const handleSequenceStarted = () => {
  showStartModal.value = false;
  fetchSequences();
  emit('sequenceStarted');
};

onMounted(fetchSequences);
</script>

<template>
  <div v-if="isSequencesEnabled">
    <!-- Header -->
    <div class="flex items-center justify-between mb-3">
      <h4 class="text-sm font-semibold text-n-slate-12">
        {{ t('PIPELINE.SEQUENCES.SECTION_TITLE') }}
      </h4>
      <button
        class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-900/30 hover:bg-violet-100 dark:hover:bg-violet-900/50 transition-colors"
        @click="showStartModal = true"
      >
        <fluent-icon icon="add" size="12" />
        {{ t('PIPELINE.SEQUENCES.START_BUTTON') }}
      </button>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="text-xs text-n-slate-11 py-2">
      {{ t('PIPELINE.SEQUENCES.LOADING') }}
    </div>

    <!-- Empty state -->
    <div
      v-else-if="sequences.length === 0"
      class="text-xs text-n-slate-11 py-2 text-center"
    >
      {{ t('PIPELINE.SEQUENCES.EMPTY_STATE') }}
    </div>

    <!-- Sequences -->
    <div v-else class="space-y-3">
      <!-- Active/Paused Sequences -->
      <div
        v-for="seq in activeSequences"
        :key="seq.id"
        class="rounded-lg border border-n-weak bg-n-alpha-1 p-3"
      >
        <!-- Sequence header -->
        <div class="flex items-start justify-between gap-2 mb-2">
          <div class="flex-1 min-w-0">
            <span class="text-xs font-medium text-n-slate-12 truncate block">
              {{ seq.definition?.name || t('PIPELINE.SEQUENCES.UNNAMED') }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ t('PIPELINE.SEQUENCES.STEP_LABEL') }} {{ seq.current_step }} /
              {{ seq.total_steps }}
            </span>
          </div>
          <!-- Status badge -->
          <span
            class="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium"
            :class="
              seq.status === 'active'
                ? 'bg-n-teal-3 text-n-teal-11'
                : 'bg-n-amber-3 text-n-amber-11'
            "
          >
            {{
              seq.status === 'active'
                ? t('PIPELINE.SEQUENCES.STATUS_ACTIVE')
                : t('PIPELINE.SEQUENCES.STATUS_PAUSED')
            }}
          </span>
        </div>

        <!-- Progress bar -->
        <div class="h-1.5 bg-n-alpha-2 rounded-full overflow-hidden mb-2">
          <div
            class="h-full bg-violet-500 rounded-full transition-all duration-300"
            :style="{ width: `${seq.progress_percentage}%` }"
          />
        </div>

        <!-- Next step info -->
        <p
          v-if="seq.next_step_at && seq.status === 'active'"
          class="text-xs text-n-slate-11 mb-2"
        >
          {{ t('PIPELINE.SEQUENCES.NEXT_STEP') }}:
          {{ formatNextStep(seq.next_step_at) }}
        </p>

        <!-- Actions -->
        <div class="flex items-center gap-2 flex-wrap">
          <!-- Mark step done — only when active and there are more steps -->
          <button
            v-if="seq.status === 'active' && seq.current_step < seq.total_steps"
            v-tooltip="{
              content: t('PIPELINE.SEQUENCES.COMPLETE_STEP_TOOLTIP'),
              delay: { show: 300, hide: 0 },
              placement: 'top',
            }"
            class="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md bg-grass-3 text-grass-11 border border-grass-6/50 hover:bg-grass-4 transition-colors disabled:opacity-50"
            :disabled="completingStepId === seq.id"
            @click="handleCompleteStep(seq)"
          >
            <fluent-icon icon="checkmark-circle" size="12" />
            {{ t('PIPELINE.SEQUENCES.COMPLETE_STEP') }}
          </button>
          <button
            v-if="seq.status === 'active'"
            class="text-xs text-n-amber-11 hover:underline"
            @click="handlePause(seq)"
          >
            {{ t('PIPELINE.SEQUENCES.ACTION_PAUSE') }}
          </button>
          <button
            v-if="seq.status === 'paused'"
            class="text-xs text-green-600 dark:text-green-400 hover:underline"
            @click="handleResume(seq)"
          >
            {{ t('PIPELINE.SEQUENCES.ACTION_RESUME') }}
          </button>
          <button
            class="text-xs text-red-500 dark:text-red-400 hover:underline ml-auto"
            @click="handleCancel(seq)"
          >
            {{ t('PIPELINE.SEQUENCES.ACTION_CANCEL') }}
          </button>
        </div>

        <!-- Steps log (last 3) -->
        <div
          v-if="seq.steps_log?.length > 0"
          class="mt-2 pt-2 border-t border-n-weak"
        >
          <p class="text-xs font-medium text-n-slate-11 mb-1">
            {{ t('PIPELINE.SEQUENCES.STEPS_LOG') }}:
          </p>
          <ul class="space-y-1">
            <li
              v-for="log in seq.steps_log.slice(-3)"
              :key="log.step_number"
              class="text-xs text-n-slate-11 flex items-center gap-1"
            >
              <fluent-icon
                icon="checkmark-circle"
                size="12"
                class="text-green-500 shrink-0"
              />
              {{ log.title }} ({{ log.activity_type }})
            </li>
          </ul>
        </div>
      </div>

      <!-- Completed/Cancelled summary -->
      <div
        v-if="completedSequences.length > 0"
        class="text-xs text-n-slate-11 mt-2"
      >
        {{ completedSequences.length }}
        {{ t('PIPELINE.SEQUENCES.COMPLETED_COUNT') }}
      </div>
    </div>

    <!-- Start Sequence Modal -->
    <StartSequenceModal
      v-if="showStartModal"
      :card-id="cardId"
      @close="showStartModal = false"
      @started="handleSequenceStarted"
    />
  </div>
</template>
